import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { contentService } from '../../services/content';
import { storageService } from '../../services/storage';
import { Beat } from '../../types/content';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import RecordingAudioCheckModal from './RecordingAudioCheckModal';
import {
  isRecordingAudioCheckPassed,
  setRecordingAudioCheckPassed,
} from '../../utils/recordingAudioCheckSession';
import {
  DEFAULT_BEAT_GAIN,
  DEFAULT_VOCAL_GAIN,
  audioBufferToMp3Blob,
  audioBufferToWavBlob,
  decodeBlobToAudioBuffer,
  estimateLatencySkewSec,
  fetchAndDecodeAudio,
  getMicConstraints,
  pickRecorderMime,
  renderVerseMix,
} from './verseAudioEngine';

interface Props {
  beat: Beat;
  onClose: () => void;
  onSuccess: () => void;
  /** When true, run mic/speaker checks before starting capture (preview beat page). */
  requireAudioCheck?: boolean;
}

const VerseRecorder: React.FC<Props> = ({ beat, onClose, onSuccess, requireAudioCheck = false }) => {
  const { authState } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedMimeType, setSelectedMimeType] = useState<string>('');
  const [selectedExtension, setSelectedExtension] = useState<string>('webm');
  const [showAudioCheck, setShowAudioCheck] = useState(false);
  const pendingStartAfterCheckRef = useRef(false);

  // --- Studio pipeline state ---
  const [usingHeadphones, setUsingHeadphones] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingFailed, setProcessingFailed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isRemixing, setIsRemixing] = useState(false);
  const [vocalLevel, setVocalLevel] = useState(DEFAULT_VOCAL_GAIN);
  const [beatLevel, setBeatLevel] = useState(DEFAULT_BEAT_GAIN);
  const [timingMs, setTimingMs] = useState(0);

  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const beatPlayerRef = useRef<HTMLAudioElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const beatSourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const recordingBeatPlayerRef = useRef<HTMLAudioElement | null>(null);

  // Dual capture: the raw vocal (studio mix input) plus the legacy live mix,
  // which is kept as a bulletproof fallback identical to the old pipeline.
  const vocalRecorderRef = useRef<MediaRecorder | null>(null);
  const legacyRecorderRef = useRef<MediaRecorder | null>(null);
  const vocalChunksRef = useRef<Blob[]>([]);
  const legacyChunksRef = useRef<Blob[]>([]);
  const vocalStopPromiseRef = useRef<Promise<Blob> | null>(null);
  const legacyStopPromiseRef = useRef<Promise<Blob> | null>(null);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const meterFillRef = useRef<HTMLDivElement | null>(null);

  const vocalBufferRef = useRef<AudioBuffer | null>(null);
  const beatBufferRef = useRef<AudioBuffer | null>(null);
  const mixBufferRef = useRef<AudioBuffer | null>(null);
  const legacyBlobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const legacyUrlRef = useRef<string | null>(null);
  /** Measured vocal-vs-beat skew (start gap + playback/capture latency). */
  const autoSkewRef = useRef(0);
  /** Bumped to invalidate in-flight async work (cancel, reset, unmount). */
  const sessionRef = useRef(0);

  const { play, setRecording } = useAudioPlayer();

  const stopMeter = () => {
    if (meterRafRef.current !== null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    analyserRef.current = null;
  };

  const startMeter = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      const currentAnalyser = analyserRef.current;
      const fill = meterFillRef.current;
      if (!currentAnalyser || !fill) {
        meterRafRef.current = null;
        return;
      }
      currentAnalyser.getByteTimeDomainData(data);
      let peak = 0;
      for (let i = 0; i < data.length; i++) {
        const v = Math.abs(data[i] - 128) / 128;
        if (v > peak) peak = v;
      }
      fill.style.width = `${Math.min(100, Math.round(peak * 130))}%`;
      fill.style.backgroundColor = peak >= 0.9 ? '#dc2626' : peak >= 0.6 ? '#f59e0b' : '#22c55e';
      meterRafRef.current = requestAnimationFrame(tick);
    };
    meterRafRef.current = requestAnimationFrame(tick);
  };

  const releaseObjectUrls = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    if (legacyUrlRef.current) {
      URL.revokeObjectURL(legacyUrlRef.current);
      legacyUrlRef.current = null;
    }
  };

  const setPreviewFromMix = useCallback((mix: AudioBuffer) => {
    const wavBlob = audioBufferToWavBlob(mix);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(wavBlob);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  const stopPlaying = useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  // Cleanup function to properly reset recording state
  const cleanupRecording = () => {
    sessionRef.current += 1;
    if (isRecording) {
      stopRecording(false);
    }
    stopMeter();
    setRecording(false);
    setAudioBlob(null);
    setError(null);
    setIsPlaying(false);
    setIsProcessing(false);
    setPreviewUrl(null);
    releaseObjectUrls();
  };

  // Reset function for "Record Again" - properly cleans up audio resources
  const resetForNewRecording = () => {
    sessionRef.current += 1;

    // Stop any current recording
    if (isRecording) {
      stopRecording(false);
    }
    stopMeter();

    // Clear state
    setAudioBlob(null);
    setError(null);
    setIsPlaying(false);
    setIsProcessing(false);
    setProcessingFailed(false);
    setIsRemixing(false);
    setPreviewUrl(null);
    setVocalLevel(DEFAULT_VOCAL_GAIN);
    setBeatLevel(DEFAULT_BEAT_GAIN);
    setTimingMs(0);
    releaseObjectUrls();

    // Drop the previous take (the decoded beat stays cached for reuse)
    vocalBufferRef.current = null;
    mixBufferRef.current = null;
    legacyBlobRef.current = null;
    vocalStopPromiseRef.current = null;
    legacyStopPromiseRef.current = null;

    // Always close and recreate audio context for fresh start
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    // Reset all audio nodes
    micSourceNodeRef.current = null;
    beatSourceNodeRef.current = null;

    // Create new audio element for recording (to avoid MediaElementSourceNode reuse issues)
    if (recordingBeatPlayerRef.current) {
      recordingBeatPlayerRef.current.pause();
      recordingBeatPlayerRef.current.src = '';
    }
    recordingBeatPlayerRef.current = new Audio(beat.file_url);
    recordingBeatPlayerRef.current.crossOrigin = "anonymous";
    recordingBeatPlayerRef.current.loop = true;

    // Ensure microphone stream is fully stopped
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      micStreamRef.current = null;
    }

    // Reset MediaRecorders
    vocalRecorderRef.current = null;
    legacyRecorderRef.current = null;
    vocalChunksRef.current = [];
    legacyChunksRef.current = [];
  };

  useEffect(() => {
    const beatPlayer = beatPlayerRef.current;
    const audioPlayer = audioPlayerRef.current;

    // Initialize beat player
    if (beat.file_url && beatPlayer) {
      beatPlayer.crossOrigin = 'anonymous';
      // Use the file_url directly (it's already a public URL)
      beatPlayer.src = beat.file_url;
      beatPlayer.loop = true;
    }

    return () => {
      beatPlayer?.pause();
      recordingBeatPlayerRef.current?.pause();
      audioPlayer?.pause();
      // Ensure recording state is cleaned up on unmount
      cleanupRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount cleanup only
  }, [beat.file_url]);

  const handleStartRecordingClick = () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    if (requireAudioCheck && !isRecordingAudioCheckPassed()) {
      pendingStartAfterCheckRef.current = true;
      setShowAudioCheck(true);
      return;
    }
    startRecording();
  };

  const handleAudioCheckPassed = () => {
    setRecordingAudioCheckPassed();
    setShowAudioCheck(false);
    if (pendingStartAfterCheckRef.current) {
      pendingStartAfterCheckRef.current = false;
      startRecording();
    }
  };

  const startRecording = async () => {
    try {
      setRecording(true);
      setError(null);

      // 1. Get user media with constraints tuned for music capture
      const micStream = await navigator.mediaDevices.getUserMedia(
        getMicConstraints(usingHeadphones)
      );
      micStreamRef.current = micStream;

      // 2. Set up Web Audio API (always create fresh context)
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
        latencyHint: 'interactive',
      });
      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        await audioContext.resume().catch(() => {});
      }

      // 3. Create audio sources
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSourceNodeRef.current = micSource;

      // Use the recording beat player (created fresh for each session)
      if (!recordingBeatPlayerRef.current) {
        recordingBeatPlayerRef.current = new Audio(beat.file_url);
        recordingBeatPlayerRef.current.crossOrigin = "anonymous";
        recordingBeatPlayerRef.current.loop = true;
      }

      // Create new MediaElementSourceNode for fresh audio context
      beatSourceNodeRef.current = audioContext.createMediaElementSource(recordingBeatPlayerRef.current);

      // 4. Monitoring + legacy mix graph
      const mixedStreamDest = audioContext.createMediaStreamDestination();
      beatSourceNodeRef.current.connect(audioContext.destination); // Play beat through speakers
      micSource.connect(mixedStreamDest); // Legacy mix: mic
      beatSourceNodeRef.current.connect(mixedStreamDest); // Legacy mix: beat

      // Live input level meter on the raw mic signal
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      micSource.connect(analyser);
      analyserRef.current = analyser;

      // 5. Pick supported container/codec
      const { mimeType, extension } = pickRecorderMime();
      setSelectedMimeType(mimeType);
      setSelectedExtension(extension);
      const baseOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};

      // 6. Two recorders: raw vocal (studio mix input) + legacy live mix (fallback)
      const vocalRecorder = new MediaRecorder(micStream, {
        ...baseOptions,
        audioBitsPerSecond: 192000,
      });
      const legacyRecorder = new MediaRecorder(mixedStreamDest.stream, {
        ...baseOptions,
        audioBitsPerSecond: 160000,
      });
      vocalRecorderRef.current = vocalRecorder;
      legacyRecorderRef.current = legacyRecorder;
      vocalChunksRef.current = [];
      legacyChunksRef.current = [];

      vocalRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          vocalChunksRef.current.push(event.data);
        }
      };
      legacyRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          legacyChunksRef.current.push(event.data);
        }
      };

      vocalStopPromiseRef.current = new Promise<Blob>((resolve) => {
        vocalRecorder.onstop = () =>
          resolve(new Blob(vocalChunksRef.current, mimeType ? { type: mimeType } : undefined));
      });
      legacyStopPromiseRef.current = new Promise<Blob>((resolve) => {
        legacyRecorder.onstop = () =>
          resolve(new Blob(legacyChunksRef.current, mimeType ? { type: mimeType } : undefined));
      });

      // 7. Start capture and note when it actually begins, so the offline mix
      // can align the vocal with the beat later.
      const recStartAt = await new Promise<number>((resolve) => {
        let settled = false;
        const finish = () => {
          if (!settled) {
            settled = true;
            resolve(performance.now());
          }
        };
        vocalRecorder.onstart = finish;
        vocalRecorder.start();
        setTimeout(finish, 500); // safety: some browsers may not fire onstart
      });
      legacyRecorder.start();

      // 8. Start the beat and measure the capture-to-beat start gap
      if (recordingBeatPlayerRef.current) {
        try { recordingBeatPlayerRef.current.currentTime = 0; } catch {}
      }
      await play(recordingBeatPlayerRef.current!);
      const beatStartAt = performance.now();
      autoSkewRef.current =
        (beatStartAt - recStartAt) / 1000 + estimateLatencySkewSec(audioContext);

      startMeter();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      setRecording(false);
      stopMeter();
      setError('Could not access microphone or initialize audio context. Please check permissions.');
    }
  };

  const stopRecording = (processTake: boolean = true) => {
    if (!isRecording) {
      return;
    }
    setIsRecording(false);
    setRecording(false);
    stopMeter();

    try {
      if (vocalRecorderRef.current && vocalRecorderRef.current.state !== 'inactive') {
        vocalRecorderRef.current.stop();
      }
    } catch {}
    try {
      if (legacyRecorderRef.current && legacyRecorderRef.current.state !== 'inactive') {
        legacyRecorderRef.current.stop();
      }
    } catch {}

    if (recordingBeatPlayerRef.current) {
      recordingBeatPlayerRef.current.pause();
      try { recordingBeatPlayerRef.current.currentTime = 0; } catch {}
    }

    // Clean up Web Audio API resources (disconnect, but keep context to reuse)
    try {
      micSourceNodeRef.current?.disconnect();
      beatSourceNodeRef.current?.disconnect();
    } catch {}

    // Suspend context instead of closing so we can safely resume and reuse
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.suspend().catch(() => {});
    }

    micSourceNodeRef.current = null;
    // Keep beatSourceNodeRef for reuse on next session

    // Clean up microphone stream
    micStreamRef.current?.getTracks().forEach(track => track.stop());
    micStreamRef.current = null;

    if (processTake) {
      void processTakeAsync();
    }
  };

  /**
   * Post-take pipeline: collect both blobs, then build the studio mix.
   * Any failure falls back to the legacy live-mix recording, which matches
   * the old pipeline exactly.
   */
  const processTakeAsync = async () => {
    const session = ++sessionRef.current;
    setIsProcessing(true);
    setProcessingFailed(false);
    try {
      const vocalPromise = vocalStopPromiseRef.current;
      const legacyPromise = legacyStopPromiseRef.current;
      if (!vocalPromise || !legacyPromise) {
        throw new Error('Recorder stop promises missing');
      }
      const [vocalBlob, legacyBlob] = await Promise.all([vocalPromise, legacyPromise]);
      if (session !== sessionRef.current) return;

      // The legacy blob is available immediately as the guaranteed fallback
      legacyBlobRef.current = legacyBlob;
      setAudioBlob(legacyBlob);

      const vocalBuffer = await decodeBlobToAudioBuffer(vocalBlob);
      vocalBufferRef.current = vocalBuffer;
      if (!beatBufferRef.current) {
        beatBufferRef.current = await fetchAndDecodeAudio(beat.file_url);
      }
      if (session !== sessionRef.current) return;

      const mix = await renderVerseMix({
        vocalBuffer,
        beatBuffer: beatBufferRef.current,
        vocalGain: DEFAULT_VOCAL_GAIN,
        beatGain: DEFAULT_BEAT_GAIN,
        vocalSkipSec: autoSkewRef.current,
      });
      if (session !== sessionRef.current) return;

      mixBufferRef.current = mix;
      setPreviewFromMix(mix);
    } catch (err) {
      console.warn('Studio mix failed; falling back to the standard recording.', err);
      if (session === sessionRef.current) {
        if (!legacyBlobRef.current && legacyStopPromiseRef.current) {
          try {
            const legacyBlob = await legacyStopPromiseRef.current;
            legacyBlobRef.current = legacyBlob;
            setAudioBlob(legacyBlob);
          } catch {}
        }
        setProcessingFailed(true);
      }
    } finally {
      if (session === sessionRef.current) {
        setIsProcessing(false);
      }
    }
  };

  // Re-render the studio mix when the user adjusts levels or timing
  useEffect(() => {
    if (!vocalBufferRef.current || !beatBufferRef.current || !mixBufferRef.current) {
      return;
    }
    const session = sessionRef.current;
    const timer = setTimeout(async () => {
      setIsRemixing(true);
      try {
        const mix = await renderVerseMix({
          vocalBuffer: vocalBufferRef.current!,
          beatBuffer: beatBufferRef.current!,
          vocalGain: vocalLevel,
          beatGain: beatLevel,
          vocalSkipSec: autoSkewRef.current + timingMs / 1000,
        });
        if (session !== sessionRef.current) return;
        mixBufferRef.current = mix;
        stopPlaying();
        setPreviewFromMix(mix);
      } catch (err) {
        console.warn('Mix update failed:', err);
      } finally {
        if (session === sessionRef.current) {
          setIsRemixing(false);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vocalLevel, beatLevel, timingMs, stopPlaying, setPreviewFromMix]);

  const playRecording = async () => {
    if (!audioPlayerRef.current) return;
    let src: string | null = previewUrl;
    if (!src && audioBlob) {
      if (!legacyUrlRef.current) {
        legacyUrlRef.current = URL.createObjectURL(audioBlob);
      }
      src = legacyUrlRef.current;
    }
    if (!src) return;
    try {
      audioPlayerRef.current.src = src;
      await audioPlayerRef.current.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Preview playback failed:', err);
    }
  };

  const buildLegacyFile = (): File | null => {
    if (!audioBlob) return null;
    return new File(
      [audioBlob],
      `verse.${selectedExtension}`,
      selectedMimeType ? { type: selectedMimeType } : undefined
    );
  };

  const handleUpload = async () => {
    if (!audioBlob && !mixBufferRef.current) return;

    setIsUploading(true);
    setError(null);

    try {
      let fileUrl: string;

      if (mixBufferRef.current) {
        // Preferred: upload the studio mix as MP3 (WAV if encoding fails)
        let processedFile: File;
        try {
          const mp3Blob = await audioBufferToMp3Blob(mixBufferRef.current, 192);
          processedFile = new File([mp3Blob], 'verse.mp3', { type: 'audio/mpeg' });
        } catch (encodeErr) {
          console.warn('MP3 encoding failed, uploading WAV instead:', encodeErr);
          const wavBlob = audioBufferToWavBlob(mixBufferRef.current);
          processedFile = new File([wavBlob], 'verse.wav', { type: 'audio/wav' });
        }

        try {
          fileUrl = await storageService.uploadVerse(processedFile);
        } catch (uploadErr) {
          // Last resort: the legacy live-mix recording (same as old pipeline)
          const legacyFile = buildLegacyFile();
          if (!legacyFile) throw uploadErr;
          console.warn('Processed verse upload failed, retrying with standard recording:', uploadErr);
          fileUrl = await storageService.uploadVerse(legacyFile);
        }
      } else {
        const legacyFile = buildLegacyFile();
        if (!legacyFile) throw new Error('No recording available to upload');
        fileUrl = await storageService.uploadVerse(legacyFile);
      }

      // Create verse record
      await contentService.createVerse({
        beat_id: beat.id,
        file_url: fileUrl,
      });

      onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading verse. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Only allow rappers and producer_rappers to access this component
  if (!authState.user || (authState.user.role !== 'rapper' && authState.user.role !== 'producer_rapper')) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-lg w-full border border-gray-200 shadow-lg">
          <p className="text-red-600 text-center">Only rappers can record verses.</p>
          <button
            onClick={onClose}
            className="w-full mt-4 py-3 px-4 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const showRecordControls = !audioBlob && !isProcessing;
  const showPreview = !!audioBlob && !isProcessing;

  return (
    <>
      <RecordingAudioCheckModal
        open={showAudioCheck}
        onClose={() => {
          pendingStartAfterCheckRef.current = false;
          setShowAudioCheck(false);
        }}
        onPassed={handleAudioCheckPassed}
        beatTitle={beat.title}
      />
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-6 max-w-lg w-full text-gray-900 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-2 text-primary">Record Verse</h2>
        {/* Disclaimers */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded mb-3 text-sm">
          <strong>Tip:</strong> Wear headphones and record in a quiet room for the cleanest verse — it keeps the beat out of your microphone.
        </div>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded mb-6 text-sm">
          <strong>Disclaimer:</strong> DHHCollab will not claim any right to your recorded verse and does not intend to misuse it. All rights to the recorded verse remain with you.
        </div>
        <p className="text-gray-600 mb-6">Recording verse for: <span className="font-semibold text-gray-900">{beat.title}</span></p>

        {/* Beat Player */}
        <audio ref={beatPlayerRef} className="hidden" />

        {/* Verse Player */}
        <audio
          ref={audioPlayerRef}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        />

        <div className="space-y-4">
          {showRecordControls && (
            <>
              {!isRecording && (
                <label className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer border border-gray-200">
                  <input
                    type="checkbox"
                    checked={usingHeadphones}
                    onChange={(e) => setUsingHeadphones(e.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">I'm wearing headphones</span>
                    <br />
                    <span className="text-gray-500">
                      Unlocks studio-quality capture. Without headphones we keep echo
                      cancellation on so the beat doesn't leak into your mic.
                    </span>
                  </span>
                </label>
              )}

              {isRecording && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Mic level</span>
                    <span className="text-sm text-red-600 font-medium animate-pulse">● REC</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      ref={meterFillRef}
                      className="h-full rounded-full"
                      style={{ width: '0%', backgroundColor: '#22c55e', transition: 'width 75ms linear' }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Green is good. If the bar stays red, move back from the mic a little.
                  </p>
                </div>
              )}

              <button
                onClick={handleStartRecordingClick}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  isRecording
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-primary hover:bg-primary-dark text-white'
                }`}
              >
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </button>
            </>
          )}

          {isProcessing && (
            <div className="p-6 bg-gray-50 rounded-lg text-center border border-gray-200">
              <div className="mx-auto mb-3 h-8 w-8 rounded-full border-4 border-gray-300 border-t-primary animate-spin" />
              <p className="text-gray-700 font-medium">Mixing your verse…</p>
              <p className="text-gray-500 text-sm mt-1">Aligning vocals with the beat and polishing the sound.</p>
            </div>
          )}

          {showPreview && (
            <div className="space-y-4">
              {previewUrl ? (
                <>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 text-center">
                    Studio mix ready — preview it and fine-tune below.
                  </div>
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Vocal volume</span>
                        <span>{Math.round(vocalLevel * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min={0.5}
                        max={2}
                        step={0.05}
                        value={vocalLevel}
                        onChange={(e) => setVocalLevel(Number(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Beat volume</span>
                        <span>{Math.round(beatLevel * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1.5}
                        step={0.05}
                        value={beatLevel}
                        onChange={(e) => setBeatLevel(Number(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Vocal timing</span>
                        <span>{timingMs > 0 ? `+${timingMs}` : timingMs} ms</span>
                      </div>
                      <input
                        type="range"
                        min={-250}
                        max={250}
                        step={10}
                        value={timingMs}
                        onChange={(e) => setTimingMs(Number(e.target.value))}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Vocals sound late? Slide right. Too early? Slide left.
                      </p>
                    </div>
                    {isRemixing && (
                      <p className="text-xs text-gray-400 text-center">Updating mix…</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-center text-gray-600">Preview your recording before uploading.</p>
                  {processingFailed && (
                    <p className="text-center text-gray-400 text-xs mt-1">
                      Enhanced mixing wasn't available for this take; using the standard recording.
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={isPlaying ? stopPlaying : playRecording}
                className="w-full py-3 px-4 rounded-lg font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                {isPlaying ? 'Stop Playing' : 'Play Recording'}
              </button>

              <div className="flex space-x-4">
                <button
                  onClick={resetForNewRecording}
                  className="flex-1 py-3 px-4 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
                >
                  Record Again
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading || isRemixing}
                  className="flex-1 py-3 px-4 rounded-lg font-medium bg-primary hover:bg-primary-dark text-white disabled:opacity-50 transition-colors"
                >
                  {isUploading ? 'Uploading...' : 'Upload Verse'}
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              cleanupRecording();
              onClose();
            }}
            className="w-full py-3 px-4 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-900 transition-colors"
          >
            Cancel
          </button>

          {error && (
            <p className="text-red-600 text-sm mt-2 text-center">{error}</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default VerseRecorder;
